<script lang="ts">
export default {
  name: 'Combobox',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  ComboboxRoot,
  ComboboxInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxItemIndicator,
  useFilter,
  ComboboxAnchor,
  ComboboxTrigger,
  ComboboxPortal,
  ComboboxVirtualizer,
} from 'reka-ui';
import { Check, ChevronDown } from '@lucide/vue';

export interface ComboboxOption {
  label: string;
  value: string;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue: string;
    options: ComboboxOption[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    inputClass?: string;
    allowFreeText?: boolean;
  }>(),
  { label: '' }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  selected: [value: ComboboxOption];
  focusin: [];
  focusout: [];
}>();

// ----------------
// refs and consts
// ----------------
const searchTerm = ref('');
const selectedOption = ref<ComboboxOption | undefined>();
const isOpen = ref(false);
// The input shows the current selection, but that text is not a search until the user
// types one: reopening a filled control should offer every option again, not just the
// one already chosen and whatever else happens to contain it.
const isFiltering = ref(false);

// Use Reka UI's filter utility
const { contains } = useFilter({ sensitivity: 'base' });

// ---------
// computed
// ---------
// Uncapped: the list is virtualized, so only the visible window is ever in the DOM.
// Reference lists here run to hundreds of rows (592 insurer groups, 1269 carriers) and
// truncating them made the tail reachable only by guessing at a search term.
const filteredOptions = computed(() => {
  if (!isFiltering.value || !searchTerm.value || searchTerm.value.trim() === '') {
    return props.options.filter((option) => !option.disabled);
  }

  const matchedOptions = props.options.filter((option) => contains(option.label, searchTerm.value) && !option.disabled);

  // If allowFreeText is enabled and there's search text but no matches, add a custom option
  if (props.allowFreeText && searchTerm.value.trim() && matchedOptions.length === 0) {
    const customOption: ComboboxOption = {
      label: searchTerm.value.trim(),
      value: searchTerm.value.trim(),
      disabled: false
    };
    return [customOption];
  }
  
  return matchedOptions;
});

// --------
// watchers
// --------
watch(
  () => props.modelValue,
  (newValue) => {
    const found = props.options.find((option) => option.value === newValue);
    if (found) {
      selectedOption.value = found;
    } else if (newValue && props.allowFreeText) {
      if (selectedOption.value?.value !== newValue) {
        selectedOption.value = { label: newValue, value: newValue };
      }
    } else {
      selectedOption.value = undefined;
    }
  },
  { immediate: true },
);

watch(
  () => props.options,
  () => {
    const found = props.options.find((option) => option.value === props.modelValue);
    if (found) {
      selectedOption.value = found;
    } else if (props.modelValue && props.allowFreeText) {
      if (selectedOption.value?.value !== props.modelValue) {
        selectedOption.value = { label: props.modelValue, value: props.modelValue };
      }
    } else {
      selectedOption.value = undefined;
    }
  },
  { immediate: true },
);

// Watch for when the user clears the search term
watch(searchTerm, (newValue, oldValue) => {
  // If the search term is cleared (empty) and we had a selection
  if (newValue === '' && oldValue !== '' && selectedOption.value) {
    // Clear the selection and close the dropdown
    selectedOption.value = undefined;
    emit('update:modelValue', '');
    isOpen.value = false;
  }
  // If the search term is empty and we're opening (from chevron click), close immediately
  else if (newValue === '' && isOpen.value && !selectedOption.value) {
    isOpen.value = false;
  }
});

// --------
// handlers
// --------
function handleSelectionChange(option: ComboboxOption | undefined) {
  if (option) {
    searchTerm.value = option.label;
    isFiltering.value = false;
    emit('update:modelValue', option.value);
    emit('selected', option);
    isOpen.value = false;
  }
}

function handleOpenChange(open: boolean) {
  isOpen.value = open;
  isFiltering.value = false;

  // When opening, set searchTerm to the current label for easier clearing
  if (open && selectedOption.value) {
    searchTerm.value = selectedOption.value.label;
  }
}

// Selecting the text means the first keystroke replaces the current value rather than
// appending to it, which is what typing into a filled control is meant to do.
function handleFocus(event: FocusEvent) {
  isOpen.value = true;
  (event.target as HTMLInputElement | null)?.select();
}

function handleInputChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  isFiltering.value = true;

  // If the input is manually cleared, ensure we clear the selection
  if (value === '' && selectedOption.value) {
    selectedOption.value = undefined;
    emit('update:modelValue', '');
    isOpen.value = false;
  }
}

</script>

<template>
  <div class="form-control w-full">
    <label v-if="label" class="label">
      <span class="label-text text-sm">{{ label }}</span>
    </label>
    <div class="relative w-full">
    <ComboboxRoot
      v-model="selectedOption"
      @update:model-value="handleSelectionChange"
      @update:open="handleOpenChange"
      :disabled="disabled"
      :ignore-filter="true"
      :open="isOpen">
      <ComboboxAnchor>
        <div class="relative">
          <ComboboxInput
            v-model="searchTerm"
            :display-value="(option: ComboboxOption | undefined) => option?.label || ''"
            :placeholder="placeholder || 'Type to search...'"
            :class="['input w-full border-solid', inputClass]"
            @focus="handleFocus"
            @focusin="emit('focusin')"
            @focusout="emit('focusout')"
            @input="handleInputChange" />
          <ComboboxTrigger class="absolute right-0 top-0 h-full w-10 flex items-center justify-center">
            <ChevronDown
              v-if="!disabled"
              :size="16"
              class="text-gray-500" />
          </ComboboxTrigger>
        </div>
      </ComboboxAnchor>

      <ComboboxPortal>
        <!-- The virtualizer measures its own DOM parent as the scroll container, so it has
             to stay a direct child of the scrolling content box, and that box has to lay
             out as a block: the popper content is a column flex container by default,
             which shrinks the virtualizer's full-height spacer down to the visible box and
             leaves nothing to scroll. Rows are a fixed height because the virtualizer
             positions by estimateSize and never re-measures -- a wrapped label would leave
             every row below it misplaced. -->
        <ComboboxContent
          v-if="isOpen && (searchTerm || !selectedOption) && filteredOptions.length > 0"
          class="z-9999 block max-h-60 w-[max(200px,var(--reka-popper-anchor-width))] max-w-[min(90vw,36rem)] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
          :position="'popper'"
          :side="'bottom'"
          :side-offset="4"
          :align="'start'"
          :avoid-collisions="true">
          <ComboboxVirtualizer
            v-slot="{ option }"
            :options="filteredOptions"
            :estimate-size="40"
            :overscan="8"
            :text-content="(item: ComboboxOption) => item.label.toLowerCase()">
            <ComboboxItem
              :value="option"
              :disabled="option.disabled"
              class="flex h-10 w-full cursor-pointer items-center justify-between gap-2 px-3 text-sm hover:bg-gray-50 data-[disabled]:cursor-not-allowed data-[highlighted]:bg-gray-50 data-[disabled]:opacity-50">
              <span
                class="flex-1 truncate text-left"
                :title="option.label">
                {{ option.label }}
              </span>
              <ComboboxItemIndicator>
                <Check :size="16" class="shrink-0 stroke-green-600" />
              </ComboboxItemIndicator>
            </ComboboxItem>
          </ComboboxVirtualizer>
        </ComboboxContent>
      </ComboboxPortal>
    </ComboboxRoot>
    </div>
  </div>
</template>
